import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { getConfig, type WidgetConfig } from '../config';
import {
  createTelegramConnectLink,
  applyTelegramConnectCoupon,
  disconnectTelegram,
  getTelegramStatus,
  updateTelegramNotifications,
  type TelegramNotificationPrefs,
  type TelegramStatusResponse,
} from '../api';
import { t } from '../i18n';

export type TelegramPanelVariant = 'account' | 'order_fc' | 'order_info';

interface Props {
  variant?: TelegramPanelVariant;
  config?: WidgetConfig;
  onApplyingChange?: (applying: boolean) => void;
  onCouponApplied?: () => void | Promise<void>;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function boundSiteLabel(status: TelegramStatusResponse): string {
  return status.bound_from_site_title || status.bound_from_site_code || '';
}

function telegramPrefText(key: string, field: 'label' | 'description', fallback: string): string {
  const translationKey = `telegram.pref.${key}.${field}`;
  const translated = t(translationKey);
  return translated === translationKey ? fallback : translated;
}

export function TelegramPanel({ variant = 'account', config: configProp, onApplyingChange, onCouponApplied }: Props) {
  const config = configProp ?? getConfig();
  const [status, setStatus] = useState<TelegramStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [botUrl, setBotUrl] = useState('');
  const [expiresHint, setExpiresHint] = useState('');
  const [notificationsSaving, setNotificationsSaving] = useState(false);
  const [disconnectConfirm, setDisconnectConfirm] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [couponApplying, setCouponApplying] = useState(false);
  const [couponApplied, setCouponApplied] = useState(false);
  const [replaceConfirm, setReplaceConfirm] = useState(false);
  const pollRef = useRef<number | null>(null);
  const autoApplyRef = useRef(false);

  const stopPoll = useCallback(() => {
    if (pollRef.current != null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const refreshStatus = useCallback(async () => {
    const res = await getTelegramStatus(config);
    if (res.status === 200 && res.result) {
      setStatus(res.result);
      if (res.result.connected) {
        setBotUrl('');
        setExpiresHint('');
        stopPoll();
      }
      return res.result;
    }
    return null;
  }, [config, stopPoll]);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError('');
      const res = await getTelegramStatus(config);
      if (!active) return;
      if (res.status === 200 && res.result) {
        setStatus(res.result);
      } else if (res.status !== 401) {
        setError(res.error || t('telegram.unavailable'));
      }
      setLoading(false);
    })();
    return () => {
      active = false;
      stopPoll();
    };
  }, [config.apiUrl, config.siteKey, stopPoll]);

  useEffect(() => {
    const offer = status?.coupon_offer;
    if (variant !== 'order_fc' || autoApplyRef.current || offer?.state !== 'active'
      || offer.source !== 'self' || offer.origin !== 'shop_checkout' || !offer.code) return;
    autoApplyRef.current = true;
    void handleCouponApply(false, 'self_checkout');
  }, [status?.coupon_offer, variant]);

  if (loading) {
    if (variant === 'order_fc') return null;
    return (
      <div class="card border-0 shadow-sm mb-4">
        <div class="card-body small text-muted">{t('telegram.statusLoading')}</div>
      </div>
    );
  }

  if (!status?.available) {
    return null;
  }

  const connected = !!status.connected;
  const showAccountExtras = connected && variant === 'account';
  const username = status.username ? `@${String(status.username).replace(/^@/, '')}` : '';
  const boundSite = boundSiteLabel(status);
  const notificationOptions = status.notification_options ?? [];
  const notificationPrefs = status.notification_prefs ?? ({} as TelegramNotificationPrefs);
  const couponOffer = status.coupon_offer;
  const couponPercent = Number(couponOffer?.percent || 20);

  const handleConnect = async () => {
    setConnecting(true);
    setError('');
    setCopied(false);
    const origin = variant === 'order_fc' ? 'shop_checkout' : variant === 'order_info' ? 'shop_order' : 'shop_account';
    const res = await createTelegramConnectLink(config, origin);
    setConnecting(false);
    if (res.status === 200 && res.result?.bot_url) {
      setBotUrl(res.result.bot_url);
      const mins = res.result.expires_in ? Math.round(res.result.expires_in / 60) : 0;
      setExpiresHint(mins > 0 ? t('telegram.expiresMinutes', { minutes: mins }) : '');
      window.open(res.result.bot_url, '_blank', 'noopener');
      stopPoll();
      pollRef.current = window.setInterval(() => { void refreshStatus(); }, 4000);
      return;
    }
    setError(res.error || t('telegram.unavailable'));
  };

  const handleCopyLink = async () => {
    if (!botUrl || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(botUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  const handlePrefToggle = async (key: keyof TelegramNotificationPrefs, enabled: boolean) => {
    const previous = { ...notificationPrefs };
    const next = { ...notificationPrefs, [key]: enabled };
    setStatus((s) => (s ? { ...s, notification_prefs: next } : s));
    setNotificationsSaving(true);
    setError('');
    const res = await updateTelegramNotifications(config, next);
    setNotificationsSaving(false);
    if (res.status === 200 && res.result) {
      setStatus((s) => (s ? {
        ...s,
        notifications_enabled: res.result!.notifications_enabled,
        notification_prefs: res.result!.notification_prefs ?? next,
        notification_options: res.result!.notification_options ?? s.notification_options,
      } : s));
      return;
    }
    setStatus((s) => (s ? { ...s, notification_prefs: previous } : s));
    setError(res.error || t('telegram.notificationsSaveError'));
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    setError('');
    const res = await disconnectTelegram(config);
    setDisconnecting(false);
    if (res.status === 200) {
      setDisconnectConfirm(false);
      setBotUrl('');
      setCouponApplied(false);
      setStatus((s) => (s ? { ...s, connected: false, username: null } : s));
      await refreshStatus();
      return;
    }
    setError(res.error || t('telegram.disconnectError'));
  };

  async function handleCouponApply(replaceExisting: boolean, mode: 'self_checkout' | 'self_explicit') {
    const code = String(status?.coupon_offer?.code || '').trim();
    if (!code || couponApplying) return;
    setCouponApplying(true);
    onApplyingChange?.(true);
    setError('');
    const replace_existing = replaceExisting;
    const res = await applyTelegramConnectCoupon(config, code, mode, replace_existing);
    setCouponApplying(false);
    onApplyingChange?.(false);
    if (res.status === 200 && !res.error) {
      setCouponApplied(true);
      setReplaceConfirm(false);
      await onCouponApplied?.();
      return;
    }
    if (!replaceExisting && /replac/i.test(String(res.error || ''))) {
      setReplaceConfirm(true);
      return;
    }
    setError(res.error || t('cartPage.couponNotApplied'));
  }

  if (variant === 'order_fc') {
    const eligible = couponOffer?.state === 'eligible';
    const activeCheckout = couponOffer?.state === 'active'
      && couponOffer.source === 'self'
      && couponOffer.origin === 'shop_checkout';
    if (!eligible && !activeCheckout && !couponApplied) return null;
    return (
      <div class="card border-primary-subtle shadow-sm mb-3">
        <div class="card-body py-3">
          <div class="d-flex align-items-start gap-3">
            <div class="text-primary fs-4 lh-1"><i class="bi bi-telegram" aria-hidden="true"></i></div>
            <div class="flex-grow-1">
              <h6 class="mb-1">{t('telegram.coupon.checkoutTitle')}</h6>
              {couponApplied ? (
                <div class="small text-success fw-semibold">{t('telegram.coupon.applied')}</div>
              ) : (
                <>
                  <p class="small text-muted mb-2">{t('telegram.coupon.checkoutBody')}</p>
                  {error && <div class="alert alert-danger py-2 small">{error}</div>}
                  {eligible && (
                    <button class="btn btn-primary btn-sm" type="button" disabled={connecting} onClick={() => void handleConnect()}>
                      {connecting ? t('telegram.connecting') : t('telegram.coupon.checkoutAction')}
                    </button>
                  )}
                  {couponApplying && <span class="small text-muted ms-2">{t('telegram.connecting')}</span>}
                  {replaceConfirm && (
                    <button class="btn btn-warning btn-sm ms-2" type="button" onClick={() => void handleCouponApply(true, 'self_checkout')}>
                      {t('telegram.coupon.replaceConfirm')}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const titleKey = variant === 'order_info' ? 'telegram.orderInfoTitle' : 'telegram.title';

  return (
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-body">
        <div class="d-flex align-items-start gap-3">
          <div class="text-primary fs-3 lh-1"><i class="bi bi-telegram" aria-hidden="true"></i></div>
          <div class="flex-grow-1">
            <h5 class="card-title mb-2">{t(titleKey)}</h5>
            {variant === 'order_info' && (
              <>
                <p class="small text-muted mb-2">{t('telegram.orderInfoIntro')}</p>
                <ul class="small text-muted mb-3 ps-3">
                  <li>{t('telegram.orderInfoItem1')}</li>
                  <li>{t('telegram.orderInfoItem2')}</li>
                  <li>{t('telegram.orderInfoItem3')}</li>
                  <li>{t('telegram.orderInfoItem4')}</li>
                </ul>
              </>
            )}
            {variant === 'account' && (
              <p class="small text-muted mb-3">{t('telegram.hint')}</p>
            )}

            {error && (
              <div class="alert alert-danger py-2 small" role="alert">{error}</div>
            )}

            {connected && (
              <div class="rounded border border-success-subtle bg-success-subtle p-3 mb-3">
                <div class="fw-semibold text-success-emphasis mb-1">{t('telegram.connected')}</div>
                {username && <div class="small text-muted mb-0">{username}</div>}
                {status.connected_at && (
                  <div class="small text-muted">{t('telegram.connectedAt')}: {formatDate(status.connected_at)}</div>
                )}
                {boundSite && (
                  <div class="small text-muted">{t('telegram.boundFrom')}: {boundSite}</div>
                )}
              </div>
            )}

            {couponOffer?.state === 'active' && (
              <div class="alert alert-success py-2 small mb-3">
                {t('telegram.coupon.active', {percent: couponPercent})}
                {couponOffer.code && <><br /><code>{couponOffer.code}</code></>}
                {variant === 'account' && couponOffer.code && couponOffer.source !== 'agent' && (
                  <button class="btn btn-success btn-sm ms-2" type="button" disabled={couponApplying} onClick={() => void handleCouponApply(false, 'self_explicit')}>
                    {t('telegram.coupon.checkoutAction')}
                  </button>
                )}
                {variant === 'account' && replaceConfirm && (
                  <button class="btn btn-warning btn-sm ms-2" type="button" disabled={couponApplying} onClick={() => void handleCouponApply(true, 'self_explicit')}>
                    {t('telegram.coupon.replaceConfirm')}
                  </button>
                )}
                {variant === 'account' && couponApplied && (
                  <div class="small fw-semibold mt-2">{t('telegram.coupon.applied')}</div>
                )}
              </div>
            )}
            {couponOffer?.state === 'redeemed' && <div class="alert alert-secondary py-2 small">{t('telegram.coupon.redeemed')}</div>}
            {couponOffer?.state === 'expired' && <div class="alert alert-secondary py-2 small">{t('telegram.coupon.expired')}</div>}

            {showAccountExtras && notificationOptions.length > 0 && (
              <div class="mb-3">
                <div class="fw-semibold small mb-2">{t('telegram.notificationsTitle')}</div>
                <p class="small text-muted mb-2">{t('telegram.notificationsHint')}</p>
                {notificationOptions.map((option) => {
                  const label = telegramPrefText(option.key, 'label', option.label);
                  const description = telegramPrefText(option.key, 'description', option.description);
                  return (
                    <div class="form-check mb-2" key={option.key}>
                      <input
                        class="form-check-input"
                        type="checkbox"
                        id={`sw-tg-pref-${option.key}`}
                        checked={!!notificationPrefs[option.key]}
                        disabled={notificationsSaving}
                        onChange={(e) => {
                          void handlePrefToggle(option.key, (e.target as HTMLInputElement).checked);
                        }}
                      />
                      <label class="form-check-label small" for={`sw-tg-pref-${option.key}`}>
                        <span class="fw-semibold">{label}</span><br />
                        <span class="text-muted">{description}</span>
                      </label>
                    </div>
                  );
                })}
                {notificationsSaving && (
                  <div class="small text-muted">{t('telegram.notificationsSaving')}</div>
                )}
              </div>
            )}

            {!connected && (
              <div class="mb-0">
                <button
                  type="button"
                  class="btn btn-primary btn-sm"
                  disabled={connecting}
                  onClick={() => { void handleConnect(); }}
                >
                  {connecting ? t('telegram.connecting') : t('telegram.connect')}
                </button>
              </div>
            )}

            {!connected && botUrl && (
              <div class="mt-3 rounded border p-3 bg-light">
                <p class="small mb-2">{t('telegram.linkReady')}</p>
                <div class="d-flex flex-wrap gap-2 mb-2">
                  <a href={botUrl} class="btn btn-primary btn-sm" target="_blank" rel="noopener noreferrer">
                    {t('telegram.openTelegram')}
                  </a>
                  <button type="button" class="btn btn-outline-secondary btn-sm" onClick={() => { void handleCopyLink(); }}>
                    {copied ? t('telegram.copied') : t('telegram.copyLink')}
                  </button>
                </div>
                {expiresHint && <p class="small text-muted mb-0">{expiresHint}</p>}
              </div>
            )}

            {showAccountExtras && (
              <div class="mt-3">
                {!disconnectConfirm ? (
                  <button
                    type="button"
                    class="btn btn-outline-danger btn-sm"
                    onClick={() => setDisconnectConfirm(true)}
                  >
                    {t('telegram.disconnect')}
                  </button>
                ) : (
                  <div class="rounded border p-3 bg-light">
                    <p class="small mb-2 mb-0">{t('telegram.disconnectConfirm')}</p>
                    <div class="d-flex flex-wrap gap-2 mt-2">
                      <button
                        type="button"
                        class="btn btn-danger btn-sm"
                        disabled={disconnecting}
                        onClick={() => { void handleDisconnect(); }}
                      >
                        {t('telegram.disconnectYes')}
                      </button>
                      <button
                        type="button"
                        class="btn btn-outline-secondary btn-sm"
                        onClick={() => setDisconnectConfirm(false)}
                      >
                        {t('telegram.disconnectCancel')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
